import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Features from './Features';
import { Helmet } from 'react-helmet';

const Home: React.FC = () => {
  return (
    <div className="relative bg-neutral-50 overflow-hidden">
      <Helmet>
        <title>Wardha Mushroom | Global Bulk Supplier & Pan-India Exporter</title>
        <meta name="description" content="Premium B2B supplier of high-grade commercial mushrooms. Specializing in high-volume domestic supply across India and global export standards." />
        <meta name="keywords" content="Top Mushroom Exporter in India, Best Mushroom Wholesaler in India, Commercial Mushroom Supply, Pan India Mushroom Bulk Distributor, Best Dried Oyster Mushroom Exporter, Premium Button Mushroom Supplier India, B2B Mushroom Cultivation, Wardha Mushroom Wholesale, Export Quality Mushrooms" />
      </Helmet>
      
      {/* Premium Hero Section */}
      <header className="relative w-full min-h-screen flex flex-col justify-center items-center bg-cover bg-center" 
        style={{ backgroundImage: 'url(/images/home.webp)', backgroundAttachment: 'fixed' }}>
        
        {/* Deep, luxurious dark overlay to make text pop and give an enterprise feel */}
        <div className="absolute inset-0 bg-gradient-to-tr from-neutral-950 via-neutral-900/80 to-transparent opacity-85"></div>

        <motion.div 
          className="relative text-center text-white max-w-4xl px-6 z-20 mt-12"
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
        >
          <motion.span 
            className="text-xs md:text-sm font-semibold tracking-widest text-emerald-400 uppercase bg-emerald-950/50 px-4 py-1.5 rounded-full border border-emerald-800/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Global Agribusiness & Supply Chain Partner
          </motion.span>

          <motion.h1 className="text-4xl md:text-7xl font-bold mt-6 mb-6 font-serif tracking-tight leading-tight">
            Cultivating Excellence.<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-emerald-200 to-white">
              Supplying the World.
            </span>
          </motion.h1>

          <motion.p className="text-lg md:text-xl mb-10 text-neutral-300 font-light max-w-2xl mx-auto leading-relaxed">
            Premier Pan-India Wholesaler and International Exporter of Premium Grade Fresh, Dried, and Custom-Processed Mushrooms.
          </motion.p>

          {/* B2B Primary Call to Action */}
          <div className="flex flex-col sm:flex-row justify-center gap-4 items-center">
            <Link to="/wholesale2" aria-label="Partner with us for wholesale supply">
              <motion.button 
                className="w-48 bg-gradient-to-r from-emerald-600 to-teal-700 text-white font-medium px-8 py-3.5 rounded-lg text-base shadow-xl shadow-emerald-950/20 hover:brightness-110 transition-all border border-emerald-500/20"
                whileHover={{ y: -3 }}
                whileTap={{ scale: 0.98 }}
              >
                Bulk Inquiry
              </motion.button>
            </Link>
            
            <Link to="/global-trade" aria-label="Inquire about global export options">
              <motion.button 
                className="w-48 bg-white/10 backdrop-blur-md text-white font-medium px-8 py-3.5 rounded-lg text-base shadow-lg border border-white/20 hover:bg-white/20 transition-all"
                whileHover={{ y: -3 }}
                whileTap={{ scale: 0.98 }}
              >
                Global Export
              </motion.button>
            </Link>
          </div>
        </motion.div>

        {/* Enterprise Services Section Grid */}
        <motion.div 
          className="relative w-full max-w-6xl mt-16 mb-12 p-4 md:p-8 z-20"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.6, ease: "easeOut" }} 
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { 
                icon: '🏢', 
                title: 'Pan-India Bulk Supply', 
                desc: 'Streamlined commercial distribution channels catering to major food processors, hospitality chains, and domestic retailers across India.', 
                btn: 'Corporate Order', 
                link: '/wholesale2' 
              },
              { 
                icon: '🚢', 
                title: 'International Export', 
                desc: 'Premium, globally compliant dried and fresh mushroom variants processed to match strict international custom and quality standards.', 
                btn: 'Export Trade Inquiry', 
                link: '/global-trade' 
              },
              { 
                icon: '🌱', 
                title: 'Contract Cultivation', 
                desc: 'Tailored agricultural volume production and private labeling options for enterprise businesses looking for specific product configurations.', 
                btn: 'Partner With Us', 
                link: '/fresh-pick' 
              }
            ].map((service, index) => (
              <motion.div 
                key={index} 
                className="group flex flex-col justify-between p-8 bg-neutral-900/40 backdrop-blur-xl border border-neutral-800/80 rounded-2xl shadow-2xl hover:border-emerald-500/40 transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 + (index * 0.2), ease: "easeOut" }}
              >
                <div>
                  <div className="w-12 h-12 flex items-center justify-center bg-neutral-800/80 rounded-xl text-2xl mb-6 border border-neutral-700/50 group-hover:bg-emerald-950 group-hover:border-emerald-800 transition-all" aria-hidden="true">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-white tracking-wide">{service.title}</h3>
                  <p className="text-sm text-neutral-400 leading-relaxed mb-6">{service.desc}</p>
                </div>

                <Link to={service.link} aria-label={service.btn} className="mt-auto">
                  <div className="inline-flex items-center text-sm font-semibold text-emerald-400 group-hover:text-emerald-300 transition-colors">
                    {service.btn} <span className="ml-2 transform group-hover:translate-x-1 transition-transform">→</span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </header>

      {/* Features Section */}
      <section id="features-section" className="relative z-10 bg-white border-t border-neutral-100">
        <Features />
      </section>
    </div>
  );
};

export default Home;